import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import popupAlert from '@images/popup-alert.png';

import { Button, Modal } from '@components/index';
import Tooltip from '@components/tooltip/Tooltip';
import { AccessMode, OrgRoleType } from '@constants/generic';
import { AUTH } from '@constants/routes';
import { STORAGE_KEYS } from '@constants/storage';
import { useAppDispatch } from '@core/store';
import { useLazyLogoutQuery } from '@features/authentication/queries/AuthQuery';
import { resetAuthState } from '@features/authentication/slices/AuthSlice';
import { resetGroupState } from '@features/group-management/slices/groupSlice';
import { resetMemberState } from '@features/member-management/slices/MemberSlice';
import { exitTrialMode } from '@features/trial-mode/slices/trialSlice';
import useIsTruncated from '@hooks/useIsTruncated';
import { resetBranchState } from '@layouts/main-header/branchSlice';
import {
  resetUserState,
  setBlacklistedModal
} from '@layouts/main-header/userSlice';
import { clearAllLocalStorageItems, getLocalStorageItem } from '@utils/storage';
import { Org } from 'types/organization';

const BLACKLIST_SUPPORT_EMAIL = 'support@cofee.app';

const ORG_NAME_TOOLTIP_ID = 'blacklisted-org-name-tooltip';

const LINK_LABEL_STYLE =
  '!text-sm !font-medium !text-cofeePrimary6 whitespace-nowrap';

interface BlacklistedModalProps {
  isOpen: boolean;
  orgList?: Org[];
  onSwitchAccount?: () => void;
}

const BlacklistedModal = ({
  isOpen,
  orgList = [],
  onSwitchAccount
}: BlacklistedModalProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Show the org the user is currently in (the blacklisted one).
  const currentOrgId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const currentOrgName = orgList.find((org) => org.id === currentOrgId)?.name;
  // Retain the last known name so the footer doesn't flicker empty while
  // logging out clears localStorage just before the page reloads.
  const lastOrgNameRef = useRef<string>();
  if (currentOrgName) lastOrgNameRef.current = currentOrgName;
  const orgName = currentOrgName || lastOrgNameRef.current;

  const userId = getLocalStorageItem(STORAGE_KEYS.USER_ID);
  const [logout] = useLazyLogoutQuery();
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);

  const { ref: orgNameRef, isTruncated: isOrgNameTruncated } =
    useIsTruncated<HTMLSpanElement>(orgName);

  const hasNonBlacklistedOrg = orgList.some(
    (org) => org.access_mode !== AccessMode.BLACKLISTED
  );

  // If the user owns any org (or is an individual account holder) hide "Create account".
  const hasOwnerOrg = orgList.some(
    (org) =>
      org.role === OrgRoleType.OWNER || org.role === OrgRoleType.INDIVIDUAL
  );

  const showSwitchAccount = hasNonBlacklistedOrg && !!onSwitchAccount;
  const showCreateAccount = !hasNonBlacklistedOrg && !hasOwnerOrg;

  const handleLogout = async () => {
    setIsLoggingOut(true);
    const res = await logout(userId);
    if ('data' in res) {
      dispatch(exitTrialMode());
      dispatch(resetGroupState());
      dispatch(resetUserState());
      dispatch(resetAuthState());
      dispatch(resetMemberState());
      dispatch(resetBranchState());
      clearAllLocalStorageItems();
      // Full reload so logout works even when the modal is shown on the
      // login route itself (where a client-side navigate would be a no-op).
      window.location.replace(AUTH.LOGIN);
    } else {
      setIsLoggingOut(false);
    }
  };

  const handleCreateAccount = () => {
    // Clear the blacklisted modal so the account-creation flow can call its APIs.
    dispatch(setBlacklistedModal(null));
    navigate(AUTH.SELECT_ACCOUNT);
  };

  return (
    <Modal isOpen={isOpen} isClickOutsideRequired={false} className="w-120">
      <div className="flex flex-col items-center px-8 pb-6 pt-8">
        <img
          src={popupAlert}
          alt="alert"
          className="mb-4 h-28 w-full object-contain"
        />
        <h2 className="mb-3 text-center text-2xl font-semibold text-cofeeNeutral10">
          Your account has been blacklisted!
        </h2>
        <p className="text-center text-base text-cofeeNeutral8">
          If you believe this is a mistake, contact{' '}
          <a
            href={`mailto:${BLACKLIST_SUPPORT_EMAIL}`}
            className="font-medium text-cofeePrimary6 underline"
          >
            {BLACKLIST_SUPPORT_EMAIL}
          </a>{' '}
          and we&apos;ll review it.
        </p>
      </div>
      <div className="flex items-center justify-center gap-x-0.5 border-t border-cofeeNeutral5 px-6 py-4 text-sm text-cofeeNeutral8">
        {orgName && (
          <>
            <span className="flex items-center gap-x-1 whitespace-nowrap">
              Signed in as
              <span
                ref={orgNameRef}
                className="max-w-[160px] truncate"
                data-tooltip-id={
                  isOrgNameTruncated ? ORG_NAME_TOOLTIP_ID : undefined
                }
              >
                {orgName}
              </span>
            </span>
            {isOrgNameTruncated && (
              <Tooltip
                id={ORG_NAME_TOOLTIP_ID}
                type="text"
                tooltipText={orgName}
                place="top"
                className="!max-w-78 whitespace-normal break-words text-center"
              />
            )}
            <span className="mx-1 text-cofeeNeutral6">|</span>
          </>
        )}
        {showSwitchAccount && (
          <>
            <Button
              variant="transparent"
              size="none"
              label="Switch account"
              handleClick={onSwitchAccount}
              labelStyle={LINK_LABEL_STYLE}
            />
            <span className="mx-0.5 text-cofeeNeutral6">&bull;</span>
          </>
        )}
        {showCreateAccount && (
          <>
            <Button
              variant="transparent"
              size="none"
              label="Create account"
              handleClick={handleCreateAccount}
              labelStyle={LINK_LABEL_STYLE}
            />
            <span className="mx-0.5 text-cofeeNeutral6">&bull;</span>
          </>
        )}
        <div className="flex items-center gap-x-1.5">
          <Button
            variant="transparent"
            size="none"
            label="Log out"
            handleClick={handleLogout}
            disabled={isLoggingOut}
            labelStyle={LINK_LABEL_STYLE}
          />
        </div>
      </div>
    </Modal>
  );
};

export default BlacklistedModal;
